import {
  type MutableRefObject,
  type ComponentProps,
  type PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react'
// eslint-disable-next-line no-restricted-imports
import { DailyProvider, useCallObject, useDaily, useMeetingState } from '@daily-co/daily-react'
import { useDailyDevLogs } from '../hooks/useDailyDevLogs'
import { useVisitLifecycle, type UseVisitLifecycleReturnType } from '../hooks/useVisitLifecycle'
import {
  useParticipantConnectionStatuses,
  type UseParticipantConnectionStatusesReturnType,
} from '../hooks/useParticipantConnectionStatuses'
import { type PatientWithVisitInfo } from 'types/types'
import { useHandleConnectToDaily } from '../hooks/useHandleConnectToDaily'
import { useDailyDisconnectMethods, type UseDailyDisconnectMethodsReturn } from '../hooks/useDailyDisconnectMethods'
import { useDailyVisitInfo } from '../hooks/useDailyVisitInfo'
import { useEnableKrispNoiseCancellation } from '../hooks/useEnableKrispNoiseCancellation'
import { isLocalDev } from '@quickmd-llc/design-system'
import { useLeaveOnRemotePartipantLeft } from '../hooks/useLeaveOnRemotePartipantLeft'
import { useInPersonVisitNotice } from '../hooks/useInPersonVisitNotice'

type InternalHookReturnsPassedToContext = UseVisitLifecycleReturnType &
  UseParticipantConnectionStatusesReturnType &
  UseDailyDisconnectMethodsReturn

export type OnStartVisitClickInVisit = (input: { isPhoneCall: boolean }) => void

interface DailyVisitContextType extends InternalHookReturnsPassedToContext {
  patientWithVisitInfo: PatientWithVisitInfo
  onStartVisitClickInVisit: OnStartVisitClickInVisit
  lastJoinAttemptDidConnectWithPatient: boolean
  isPhoneCall: boolean
  isVideoCall: boolean
  visitIsPhoneCallOnly: boolean
  isPrepMode: boolean
  isInitiatingCall: boolean
  preferredAudioOn: boolean
  preferredVideoOn: boolean
  setPreferredAudioOn: (on: boolean) => void
  setPreferredVideoOn: (on: boolean) => void
  /** Ref for a function (e.g. save notes) to run before ending the visit call. Set from VisitCallRoom. */
  beforeEndVisitCallRef: MutableRefObject<(() => Promise<void>) | null>
  /**
   * In-person notice (QMD-4666). The state lives here so an acknowledgement outlives the visit
   * pop-up closing and reopening, but `VisitPopUpModals` renders the modal. Rendering it here
   * puts its portal in the document *before* the visit pop-up's, and since both sit at
   * `z-modal` the pop-up then covers the notice — it is still open, just underneath.
   */
  isInPersonNoticeOpen: boolean
  confirmInPersonNotice: () => void
}

export const Context = createContext<DailyVisitContextType | undefined>(undefined)

// TODO permissions hook would be good here. does not appear to be in previous implementation though so will wait for
// refactor.

type DailyVisitProviderContentProps = PropsWithChildren & {
  patientWithVisitInfo: PatientWithVisitInfo
  onStartVisitClickInVisit: OnStartVisitClickInVisit
  visitIsPhoneCallOnly: boolean
  isPrepMode?: boolean
}

function DailyVisitProviderContent({
  children,
  patientWithVisitInfo,
  onStartVisitClickInVisit,
  visitIsPhoneCallOnly,
  isPrepMode = false,
}: DailyVisitProviderContentProps) {
  const [preferredAudioOnState, setPreferredAudioOnState] = useState<boolean>(true)
  const [preferredVideoOnState, setPreferredVideoOnState] = useState<boolean>(true)
  const [isInitiatingCall, setIsInitiatingCall] = useState(false)

  // Opens with the visit, not with the call — the notice blocks the prep screen until the provider
  // acknowledges it, so there is nothing to intercept here.
  const { isNoticeOpen, confirmNotice } = useInPersonVisitNotice(patientWithVisitInfo)

  const wrappedOnStartVisitClickInVisit: OnStartVisitClickInVisit = useCallback(
    (input) => {
      setIsInitiatingCall(true)
      onStartVisitClickInVisit(input)
    },
    [onStartVisitClickInVisit],
  )

  const beforeEndVisitCallRef = useRef<(() => Promise<void>) | null>(null)
  const { participantConnectionStatuses } = useParticipantConnectionStatuses()

  const { lastJoinAttemptDidConnectWithPatient } = useHandleConnectToDaily({
    patientWithVisitInfo,
    preferredAudioOn: preferredAudioOnState,
    preferredVideoOn: preferredVideoOnState,
    isPrepMode,
  })

  const dailyDisconnectMethods = useDailyDisconnectMethods({
    onStartVisitClickInVisit: wrappedOnStartVisitClickInVisit,
    beforeEndVisitCallRef,
  })

  const { endVisitCall } = dailyDisconnectMethods

  const visitLifecycleReturn = useVisitLifecycle({
    lastJoinAttemptDidConnectWithPatient,
    participantConnectionStatuses,
    endVisitCall,
  })

  useDailyDevLogs({ show: isLocalDev })
  useEnableKrispNoiseCancellation()

  const dailyVisitInfo = useDailyVisitInfo()

  useLeaveOnRemotePartipantLeft()

  const callObject = useDaily()
  const meetingState = useMeetingState()

  const setPreferredAudioOn = useCallback(
    (on: boolean) => {
      setPreferredAudioOnState(on)
      if (meetingState === 'joined-meeting') {
        callObject?.setLocalAudio(on)
      }
    },
    [callObject, meetingState],
  )

  const setPreferredVideoOn = useCallback(
    (on: boolean) => {
      setPreferredVideoOnState(on)
      if (meetingState === 'joined-meeting') {
        callObject?.setLocalVideo(on)
      }
    },
    [callObject, meetingState],
  )

  useEffect(() => {
    if (meetingState === 'joined-meeting') {
      callObject?.setLocalAudio(preferredAudioOnState)
      callObject?.setLocalVideo(dailyVisitInfo?.isVideoCall ? preferredVideoOnState : false)
    }
  }, [callObject, meetingState, preferredAudioOnState, preferredVideoOnState, dailyVisitInfo?.isVideoCall])

  // Clear isInitiatingCall once Daily SDK starts loading/joining
  useEffect(() => {
    if (['loading', 'joining-meeting', 'joined-meeting'].some((s) => s === meetingState)) {
      setIsInitiatingCall(false)
    }
  }, [meetingState])

  const value = useMemo(
    () => ({
      lastJoinAttemptDidConnectWithPatient,
      patientWithVisitInfo,
      onStartVisitClickInVisit: wrappedOnStartVisitClickInVisit,
      ...visitLifecycleReturn,
      ...dailyDisconnectMethods,
      participantConnectionStatuses,
      isPhoneCall: !!dailyVisitInfo?.isPhoneCall,
      isVideoCall: !!dailyVisitInfo?.isVideoCall,
      visitIsPhoneCallOnly,
      isPrepMode,
      isInitiatingCall,
      preferredAudioOn: preferredAudioOnState,
      preferredVideoOn: preferredVideoOnState,
      setPreferredAudioOn,
      setPreferredVideoOn,
      beforeEndVisitCallRef,
      isInPersonNoticeOpen: isNoticeOpen,
      confirmInPersonNotice: confirmNotice,
    }),
    [
      lastJoinAttemptDidConnectWithPatient,
      patientWithVisitInfo,
      wrappedOnStartVisitClickInVisit,
      visitLifecycleReturn,
      participantConnectionStatuses,
      dailyDisconnectMethods,
      dailyVisitInfo,
      visitIsPhoneCallOnly,
      isPrepMode,
      isInitiatingCall,
      preferredAudioOnState,
      preferredVideoOnState,
      setPreferredAudioOn,
      setPreferredVideoOn,
      beforeEndVisitCallRef,
      isNoticeOpen,
      confirmNotice,
    ],
  )
  return <Context.Provider value={value}>{children}</Context.Provider>
}

type DailyVisitProviderProps = Omit<ComponentProps<typeof DailyProvider>, 'callObject'> & DailyVisitProviderContentProps

export function DailyVisitProvider({
  children,
  patientWithVisitInfo,
  onStartVisitClickInVisit,
  visitIsPhoneCallOnly,
  isPrepMode,
  ...rest
}: DailyVisitProviderProps) {
  const callObject = useCallObject({})

  return (
    <DailyProvider callObject={callObject} {...rest}>
      {/*
       // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered */}
      <DailyVisitProviderContent
        patientWithVisitInfo={patientWithVisitInfo}
        onStartVisitClickInVisit={onStartVisitClickInVisit}
        visitIsPhoneCallOnly={visitIsPhoneCallOnly}
        isPrepMode={isPrepMode}>
        {children}
      </DailyVisitProviderContent>
    </DailyProvider>
  )
}
